import { generateUUID } from '@core/utils/uuid';
import { getStoreUser, updateStoreUser } from '@core/utils/store';
import {
    createSessionStore,
    getSessionStoreUser,
    updateSessionStoreUser,
} from '@core/utils/sessionStore';
import { FREE_PASS_USERNAME, WESOG_USER_PARAMS } from '@core/constants/USER';
import { getTokensFromUsernameAndPassword } from './oauth';
import { delayTranslate } from '@core/i18n/window-language';
import { showNotification } from './notification-messages';
import { NOTIFICATIONS_TYPE } from '@core/constants/NOTIFICATIONS';
import { translate as t } from '@core/i18n/window-language';
import { TRIGGER_UPDATE_USER_INFO_HEADER_EVENT } from '@core/constants/EVENTS';
import { OAUTH_CODE_KEY } from '@core/constants/API';

/**
 * Keep the user info in local Storage.
 * @type {boolean}
 */
const KEEP_USER_INFO = true;

/**
 * Get the user info.
 * @returns {{
 *  username: string,
 *  access_token: string,
 *  searcherHistory: Array<object>,
 *  lastName: string,
 *  email: string,
 *  id: string,
 *  language: string,
 *  phone: string,
 *  pseudo: string,
 *  lightbox: Array<object>,
 * }} - The user info.
 */
export const getWesogUserInfo = () => {
    const /** @type {Object} */ user = KEEP_USER_INFO
            ? getStoreUser()
            : getSessionStoreUser();

    if (!user) {
        createWesogUserInfo();
    }

    return user;
};

/**
 * Remove the user info from local or session Storage and create a new empty one.
 * @returns {void}
 */
export const removeWesogUserInfo = () => {
    createSessionStore();
    createWesogUserInfo();
};

/**
 * Update the user info in local or session Storage.
 * @param {Object} user - The user info.
 * @returns {void}
 */
export const updateWesogUserInfo = (user) => {
    KEEP_USER_INFO ? updateStoreUser(user) : updateSessionStoreUser(user);
};

/**
 * Create the user info in local or session Storage.
 * @type {function}
 * @param {string} [access_token] - The access token. Default is an empty string.
 * @param {string} [refresh_token] - The refresh token. Default is an empty string.
 * @param {string} [username] - The username. Default is an empty string.
 * @param {number} [expires_in] - The expires in. Default is 0.
 * @returns {void}
 */
export const createWesogUserInfo = (
    access_token = '',
    refresh_token = '',
    username = '',
    expires_in = 0,
) => {
    const /** @type {Object} */ user = createUserObj(
            username,
            access_token,
            refresh_token,
            expires_in,
        );

    KEEP_USER_INFO ? updateStoreUser(user) : updateSessionStoreUser(user);
};

/**
 * Add a new element to the searcher history.
 * @param {Array<object>} searcherHistory - The searcher history.
 * @param {Object|string} newElement - The new element.
 * @returns {Array<object>} - The searcher history.
 */
export const addNewSearcherHistoryElement = (searcherHistory, newElement) => {
    /* Check if the newElement has a blob data in the image field, in that case, check also if we have an element with a blob data in the searcherHistory and update it */
    if (typeof newElement.image[0] === 'object') {
        let /** @type {Boolean} */ updateSomeElement = false;
        searcherHistory = searcherHistory.map((element) => {
            if (typeof element.image[0] === 'object') {
                element = newElement;
                updateSomeElement = true;
            }

            return element;
        });

        if (!updateSomeElement) {
            searcherHistory.unshift(newElement); // Add new element at the beginning
        }
    } else {
        searcherHistory.unshift(newElement); // Add new element at the beginning
    }

    if (searcherHistory.length > 3) {
        searcherHistory.pop(); // Remove the last element if the array length exceeds 10
    }

    return searcherHistory;
};

/**
 * Add a new element to the lightbox.
 * @param {Array<object>} lightbox - The lightbox.
 * @param {string} newElement - The new element.
 * @returns {Array<object>} - The lightbox.
 */
export const addNewLightboxElement = (lightbox, newElement) => {
    lightbox.unshift({
        id: generateUUID(),
        title: newElement,
        images: [],
        date: new Date().toLocaleDateString('es-ES', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        }),
        lightbox: true,
    });

    return lightbox;
};

/**
 * Check if the user is logged in.
 * @returns {boolean} - True if the user is logged in, false otherwise.
 */
export const isUserLoggedIn = () => {
    /** @type {{ username: string, access_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string, lightbox: string}} */
    const user = getWesogUserInfo();

    return user && user.id && user.id !== FREE_PASS_USERNAME;
};

/**
 * Check if the user has tokens.
 * @returns {boolean} - True if the user has tokens, false otherwise.
 */
export const hasUserTokens = () => {
    /** @type {{ username: string, access_token: string, searcherHistory: string, lastName: string, email: string, id: string, language: string, phone: string, pseudo: string, lightbox: string}} */
    const user = getWesogUserInfo();

    return user && user.access_token;
};

/**
 * Check the user status.
 * @returns {Promise<void>}
 */
export const checkUserStatus = async () => {
    /** @type {URLSearchParams} */
    const urlParams = new URLSearchParams(window.location.search);

    if (!hasUserTokens() && !urlParams.has(OAUTH_CODE_KEY)) {
        try {
            await getTokensFromUsernameAndPassword('', '');
        } catch (error) {
            console.error('Error get token from free pass', error);

            delayTranslate(() => {
                showNotification(
                    t('notification.errors.getTokensError'),
                    NOTIFICATIONS_TYPE.ERROR,
                );
            });
        }
    }

    document.dispatchEvent(new CustomEvent(TRIGGER_UPDATE_USER_INFO_HEADER_EVENT));
};

/**
 * Create a user object.
 * @param {string} [username] - The username. Default is an empty string.
 * @param {string} [access_token] - The access token. Default is an empty string.
 * @param {string} [refresh_token] - The refresh token. Default is an empty string.
 * @param {string} [expires_in] - The expires in. Default is an empty string.
 * @returns {Object} - The user object.
 */
export const createUserObj = (
    username = '',
    access_token = '',
    refresh_token = '',
    expires_in = '',
) => {
    return {
        [WESOG_USER_PARAMS.USERNAME]: username,
        [WESOG_USER_PARAMS.ACCESS_TOKEN]: access_token,
        [WESOG_USER_PARAMS.REFRESH_TOKEN]: refresh_token,
        [WESOG_USER_PARAMS.SEARCHER_HISTORY]: [],
        [WESOG_USER_PARAMS.LAST_NAME]: '',
        [WESOG_USER_PARAMS.EMAIL]: '',
        [WESOG_USER_PARAMS.ID]: '',
        [WESOG_USER_PARAMS.LANGUAGE]: '',
        [WESOG_USER_PARAMS.PHONE]: '',
        [WESOG_USER_PARAMS.PSEUDO]: '',
        [WESOG_USER_PARAMS.LIGHTBOX]: [],
        [WESOG_USER_PARAMS.EXPIRES_IN]: expires_in,
        [WESOG_USER_PARAMS.CART_UIDS]: [],
    };
};
